<template>
  <div class="books-edit" v-bind:class="{ splitView: isSplitView }">
    <div class="edit section">
      <b-form @submit="onSubmit" class="inner-section">
        <h1 v-if="postData">{{name}} bearbeiten</h1>
        <h1 v-else>{{name}} hinzufügen</h1>

        <b-button
          v-b-tooltip.hover
          type="submit"
          variant="primary"
          title="Speichert die Daten in der Datenbank">
          Speichern
        </b-button>

        <b-button
          v-b-tooltip.hover
          v-on:click="onReset"
          variant="warning"
          title="Setzt die Seite auf den letzten Stand zurück">
          Zurücksetzen
        </b-button>

        <b-button
          v-b-tooltip.hover
          v-on:click="areYouSure"
          variant="danger"
          title="Unwiderrufliche Löschung">
          {{name}} löschen
        </b-button>

        <b-button
          v-b-tooltip.hover
          v-if="!isSplitView"
          variant="secondary"
          title="Teilt den Bildschirm in Maske und Vorschau wenn möglich"
          v-on:click="toggleSplitView()">
          Geteilte Ansicht aktivieren
        </b-button>

        <b-button
          v-b-tooltip.hover
          v-else
          variant="outline-secondary"
          title="Zurück zur Ursprungsansicht"
          v-on:click="toggleSplitView()">
          Geteilte Ansicht deaktivieren
        </b-button>

        <hr>

        <b-alert v-model="savingAlert">
          <b-spinner small></b-spinner> Speichern
        </b-alert>

        <b-alert v-model="savedAlert" variant="success" dismissible>
          Erfolgreich gespeichert!
        </b-alert>

        <b-alert v-model="errorAlert" variant="danger" dismissible>
          Fehler beim speichern!
        </b-alert>

        <b-tabs content-class="mt-3">
          <!-- Allgemein -->
          <b-tab title="Info" active>

            <b-form-group
              id="input-group-1"
              label-for="input-1"
              v-b-tooltip.hover
              title="Titel">

              <b-form-input
                id="input-1"
                v-model="postData.title"
                required
                placeholder="Titel">
              </b-form-input>
            </b-form-group>

            <b-form-group
              id="input-group-2"
              label-for="input-2"
              v-b-tooltip.hover
              title="Untertitel">

              <b-form-input
                id="input-2"
                v-model="postData.subtitle"
                placeholder="Untertitel">
                </b-form-input>
            </b-form-group>

            <b-form-group
              id="input-group-3"
              label-for="input-3"
              description="Dient zur Identifizierung und taucht im Link auf. Muss mindestens 5 Zeichen lang sein."
              v-b-tooltip.hover
              title="Slug">

              <b-form-input
                id="input-3"
                v-model="postData.slug"
                required
                placeholder="Slug">
                </b-form-input>
            </b-form-group>

            <b-form-group
              id="input-group-author"
              label-for="input-author"
              v-b-tooltip.hover
              title="Autor">

              <b-form-select
                id="input-author"
                v-model="postData.authorSlug"
                :value="postData.authorSlug"
                required>

                <option slot="first" :value="null">
                  Autor auswählen...
                </option>

                <option
                  v-for="author in authors"
                  :key="author.slug"
                  :value="author.slug">

                    {{author.name.title + " " + author.name.name + " " + author.name.surname }}
                  </option>
                </b-form-select>
            </b-form-group>

            <b-form-group
              id="input-group-product"
              label-for="input-product"
              v-b-tooltip.hover
              title="DK-Produkt">

              <b-form-select
                id="input-product"
                :value="postData.linkedProduct.slug"
                @change="selectedProduct">

                <option slot="first" :value="''">
                  Produkt auswählen...
                </option>

                <option
                  v-for="product in products"
                  :key="product.slug"
                  :value="product.slug">

                    {{ product.name + " " + toLocaleString(product.price) + " €" }}
                  </option>
                </b-form-select>
            </b-form-group>

            <b-form-group
              id="input-group-second-product"
              label-for="input-second-product"
              v-b-tooltip.hover
              title="DK-Produkt">

              <b-form-select
                id="input-product"
                :value="postData.secondLinkedProduct.slug"
                @change="selectedSecondProduct">

                <option slot="first" :value="''">
                  Produkt auswählen...
                </option>

                <option
                  v-for="product in products"
                  :key="product.slug"
                  :value="product.slug">

                    {{ product.name + " " + toLocaleString(product.price) + " €" }}
                  </option>
                </b-form-select>
            </b-form-group>

            <b-form-group
              id="input-group-category"
              label-for="input-category"
              v-b-tooltip.hover
              description="Bestimmt die Platzierung."
              title="Kategorie">

              <b-form-select
                id="input-category"
                :value="postData.category"
                @change="selectedCategory">

                <option
                  v-for="(category, key) in categories"
                  :key="key"
                  :value="key">

                    {{ category }}
                  </option>
                </b-form-select>
            </b-form-group>

            <b-form-group>
              <input type="checkbox" class="form-check-input" id="form-input-is-light-theme" v-model="postData.isLightTheme">
              <label class="form-check-label" for="form-input-is-light-theme">Kein Banner</label>
            </b-form-group>

            <!-- <b-form-group>
              <input type="checkbox" class="form-check-input" id="form-input-is-published" v-model="postData.published">
              <label class="form-check-label" for="form-input-is-published">Veröffentlicht</label>
            </b-form-group> -->
          </b-tab>

          <!-- Bild -->
          <b-tab title="Bild">
            <b-row>
              <b-col col md="6">
                <b-form-group>
                  <input type="checkbox" class="form-check-input" id="form-input-show-image-in-post" v-model="postData.image.showInPost">
                  <label class="form-check-label" for="form-input-show-image-in-post">Vorschaubild anzeigen</label>
                </b-form-group>

                <b-form-group
                  id="input-group-preview-image-description"
                  label-for="input-preview-image-description"
                  v-b-tooltip.hover
                  title="Bildbeschreibung">

                  <b-form-input
                    id="input-preview-image-description"
                    v-model="postData.image.description"
                    placeholder="Bildbeschreibung">
                  </b-form-input>
                </b-form-group>

                <RwvImageCropper
                  @cropImage="cropImage"
                  @resizedCropImage="resizedCropImage"
                  @deleteImage="deleteImage"
                  :targetFolder="postData.slug"
                  :srcImage="postData.image.data"
                  :index="99">
                </RwvImageCropper>
              </b-col>
              <b-col col md="6">
                <slot name="inListPreview"></slot>
              </b-col>
            </b-row>
          </b-tab>

          <!-- Inhalt -->
          <b-tab title="Inhalt">
            <p class="text-muted small">
              Der jeweilige Block kann zum ändern der Reihenfolge nach unten gezogen werden.
            </p>
            <draggable v-model="postData.content">
              <RwvContentEdit
                v-for="(content, index) in postData.content"
                :key="index"
                :isDraggable="true"
                :content="content"
                :index="index"
                :postSlug="$attrs.postSlug"
                @delete="deleteContent"></RwvContentEdit>
            </draggable>

            <b-button
              variant="outline-primary"
              v-on:click="addContent()">
              Inhalt hinzufügen
            </b-button>
          </b-tab>

          <!-- Varianten -->
          <b-tab title="Autos">
            <p class="text-muted small">
              Der jeweilige Block kann zum ändern der Reihenfolge nach unten gezogen werden.
            </p>
            <draggable v-model="postData.variants">
              <div class="multiple carVariants"
                v-for="(variant, index) in postData.variants"
                :key="variant.slug">
                <h6>Autovariante {{ index+1 }}</h6>
                <div v-if="!postData.variants[index].model.manufacturer">
                  <h3>
                    Fehlerhafte Modellzuweisung
                  </h3>
                  <p>Bitte löschen sie diese Modellzuweisung und setzen Sie sie erneut.</p>
                </div>
                <b-form-row v-if="postData.variants[index].model.manufacturer">
                  <b-col>
                    <b-form-group
                      :id="'input-group-manufacturer-' + index"
                      :label-for="'input-manufacturer-' + index"
                      v-b-tooltip.hover
                      title="Hersteller">

                      <b-form-select
                        :id="'input-manufacturer-' + index"
                        v-model="postData.variants[index].model.manufacturer"
                        :value="variant.model.manufacturer"
                        v-on:change="changedManufacturer(index)"
                        required>

                        <option slot="first" :value="emptyManufacturer">
                          Bitte auswählen...
                        </option>

                        <option
                          v-for="manufacturer in manufacturers"
                          :key="manufacturer.slug"
                          :value="manufacturer">

                            {{manufacturer.name}}
                          </option>
                        </b-form-select>
                    </b-form-group>
                  </b-col>
                  <b-col cols="1">
                    <b-button
                      size="sm"
                      block
                      variant="outline-primary"
                      v-on:click="addCarComponent(index, 'Manufacturer', 'Neuen Hersteller hinzufügen')"
                      v-b-tooltip.hover
                      title="Neuen Hersteller hinzufügen">
                      <i class="material-icons">add</i>
                    </b-button>
                  </b-col>
                  <b-col>
                    <b-form-group
                      :id="'input-group-model-' + index"
                      :label-for="'input-model-' + index"
                      v-b-tooltip.hover
                      title="Modell">

                      <b-form-select
                        :id="'input-model-' + index"
                        v-model="postData.variants[index].model"
                        :value="variant.model"
                        v-on:change="changedModel(index)"
                        required>

                        <option slot="first" :value="emptyModel">
                          Bitte auswählen...
                        </option>

                        <option
                          v-for="model in filteredModels(postData.variants[index].model.manufacturer.slug)"
                          :key="model.slug"
                          :value="model">

                            {{model.name}}
                        </option>
                      </b-form-select>
                    </b-form-group>
                  </b-col>
                  <b-col cols="1">
                    <b-button
                      size="sm"
                      block
                      variant="outline-primary"
                      v-on:click="addCarComponent(index, 'Model', 'Neues Modell hinzufügen')"
                      v-b-tooltip.hover
                      title="Neues Modell hinzufügen">
                      <i class="material-icons">add</i>
                    </b-button>
                  </b-col>
                </b-form-row>
                <b-form-row>
                  <b-col v-if="postData.variants[index].model.manufacturer">
                    <b-form-group
                      :id="'input-group-variant-' + index"
                      :label-for="'input-variant-' + index"
                      v-b-tooltip.hover
                      title="Baujahr">

                      <b-form-select
                        :id="'input-variant-' + index"
                        v-model="postData.variants[index]"
                        :value="variant"
                        required>

                        <option slot="first" :value="emptyVariant">
                          Bitte auswählen...
                        </option>


                        <option
                          v-for="variant in filteredVariants(postData.variants[index].model.slug)"
                          :key="variant.slug"
                          :value="variant">

                          {{variant.year + variant.variant}}
                        </option>
                      </b-form-select>
                    </b-form-group>
                  </b-col>
                  <b-col cols="1" v-if="postData.variants[index].model.manufacturer">
                    <b-button
                      size="sm"
                      block
                      variant="outline-primary"
                      v-on:click="addCarComponent(index, 'Variant', 'Neues Bajahr hinzufügen')"
                      v-b-tooltip.hover
                      title="Neues Bajahr hinzufügen">
                      <i class="material-icons">add</i>
                    </b-button>
                  </b-col>
                  <b-col cols="2">
                    <b-button
                      block
                      size="sm"
                      variant="outline-danger"
                      v-on:click="deleteVariant(index)"
                      v-b-tooltip.hover
                      title="Auto entfernen">
                      <i class="material-icons">delete</i>
                    </b-button>
                  </b-col>
                </b-form-row>
              </div>
            </draggable>

            <b-button
              variant="outline-primary"
              v-on:click="addVariant()">
              Modell hinzufügen
            </b-button>
          </b-tab>

          <slot name="tabs"></slot>

          <slot></slot>
        </b-tabs>
      </b-form>
    </div>

    <RwvPost
      :post="postData"
      :preview="true"
      :isBook="isBook"
      :isLightTheme="postData.isLightTheme"></RwvPost>

    <!-- Add Modal -->
    <b-modal ref="modal-add" :title="addModal.title" hide-footer>
      <RwvEditManufacturer
        v-if="addModal.componentToEdit == 'Manufacturer'"
        @close="closeModalAdd"
        :isModal="true"/>
      <RwvEditModel
        v-else-if="addModal.componentToEdit == 'Model'"
        :srcManufacturerSlug="postData.variants[addModal.index].model.manufacturerSlug"
        @close="closeModalAdd"
        :isModal="true"/>
      <RwvEditVariant
        v-else-if="addModal.componentToEdit == 'Variant'"
        :srcManufacturerSlug="postData.variants[addModal.index].model.manufacturerSlug"
        :srcModelSlug="postData.variants[addModal.index].modelSlug"
        @close="closeModalAdd"
        :isModal="true"/>
    </b-modal>

    <!-- Add Modal 'Are you sure?'' -->
    <b-modal
      ref="modal-are-you-sure"
      title="Wirklich löschen?"
      ok-title="Löschen"
      ok-variant="danger"
      @ok="onDelete"
      cancel-title="Abbrechen">
      <p class="my-4">Es erfolgt dabei eine unwiderrufliche Löschung.</p>
    </b-modal>

    <!-- Error Modal -->
    <b-modal id="modal-error" :title="error.name" hide-footer>
      <template slot="modal-title" variant="danger">
        {{error.name}}
      </template>
      <p class="my-4">{{error.message}}</p>

      <b-button v-b-toggle.collapse-1 variant="primary">Mehr Details</b-button>
      <b-collapse id="collapse-1" class="mt-2">
        <b-card>
          <p class="card-text">{{error.stack}}</p>
        </b-card>
      </b-collapse>
    </b-modal>
  </div>
</template>

<script>
import { mapGetters } from "vuex"
import store from "@/store";
import RwvPost from "@/components/Posts/Post";
import RwvImageCropper from "@/components/ImageCropper";
import RwvContentEdit from "@/components/Contents/ContentEdit";
import draggable from 'vuedraggable'
import RwvVariantSelection from '@/components/VariantSelection.vue'
import RwvEditVariant from '@/components/Admin/EditVariant.vue'
import RwvEditModel from '@/components/Admin/EditModel.vue'
import RwvEditManufacturer from '@/components/Admin/EditManufacturer.vue'

import {
  slugify
} from "@/common/slugify"
import {
  FETCH_AUTHORS,
  FETCH_PRODUCTS,
  FETCH_PRODUCT,
  FETCH_MANUFACTURERS,
  FETCH_MODELS,
  FETCH_VARIANTS
} from "@/store/actions.type";

export default {
  title () {
    return `Bearb.: ${this.postData.slug} | So wird's gemacht`
  },
  components: {
    RwvPost,
    RwvImageCropper,
    RwvContentEdit,
    RwvVariantSelection,
    RwvEditVariant,
    RwvEditModel,
    RwvEditManufacturer,
    draggable
  },
  props: {
    postData: {
      type: Object,
      required: true
    },
    isBook: {
      type: Boolean,
      default: false
    },
    savingAlert: {
      type: Boolean,
      default: false
    },
    savedAlert: {
      type: Boolean,
      default: false
    },
    errorAlert: {
      type: Boolean,
      default: false
    },
    error: {
      type: Object
    },
    categories: {
      type: Object,
      required: true
    }
  },
  data () {
    return {
      isSplitView: true,
      emptyVariant: {},
      emptyModel: {},
      emptyManufacturer: {},
      addModal: {
        title: "",
        componentToEdit: "",
        index: 0
      },
      emptyContent: {
        type: "",
        data: ""
      },
    }
  },
  computed: {
    ...mapGetters(["authors", "products", "product", "authorsCount", "manufacturers", "variants", "models"]),
    name() {
      if (this.postData.category) {
        return this.categories[this.postData.category]
      } else {
        if (this.isBook) {
          return "Buch"
        } else {
          return "Artikel"
        }
      }
    }
  },
  mounted() {
    this.fetchAuthors()
    this.fetchProducts()
    this.fetchManufacturers()
    this.fetchModels()
    this.fetchVariants()

  },
  created() {
    this.emptyManufacturer = {
        slug: "",
        name: ""
      }
    this.emptyModel = {
        slug: "",
        name: "",
        manufacturer: this.emptyManufacturer
      }
    this.emptyVariant = {
        variant: "",
        year: "",
        slug: "",
        model: this.emptyModel
      }
  },
  methods: {
    fetchAuthors() {
      this.$store.dispatch(FETCH_AUTHORS);
    },
    fetchProducts() {
      this.$store.dispatch(FETCH_PRODUCTS);
    },
    fetchManufacturers() {
      this.$store.dispatch(FETCH_MANUFACTURERS);
    },
    changedManufacturer(index) {
      this.changedModel()
      this.postData.variants[index].model.name = ""
    },
    fetchModels(slug) {
      // if (slug) {
      //   this.$store.dispatch(FETCH_MODELS, {"where":{"manufacturerSlug":{"like":slug}}});
      // } else {
        this.$store.dispatch(FETCH_MODELS,
          {
            include: "manufacturer"
          });
      // }
    },
    changedModel(index) {
      console.log(this.postData.variants[index])
      this.postData.variants[index].variant = ""
      this.postData.variants[index].year = ""
    },
    filteredModels(slug) {
      return this.models.filter(function (el) {
        return el.manufacturerSlug == slug
      });
    },
    filteredVariants(slug) {
      return this.variants.filter(function (el) {
        return el.modelSlug == slug
      });
    },
    fetchVariants(slug) {
      this.$store.dispatch(FETCH_VARIANTS,
        {
          include: {
            relation: "model",
            scope: {
              include: "manufacturer"
            }
          }
        });
    },
    addCarComponent(index, componentToEdit, title) {
      this.addModal.componentToEdit = componentToEdit
      this.addModal.title = title
      this.addModal.index = index

      this.$refs['modal-add'].show()
    },
    closeModalAdd() {
      this.fetchManufacturers()
      this.fetchModels()
      this.fetchVariants()
      this.$refs['modal-add'].hide()
    },
    addContent() {
      this.postData.content.push(JSON.parse(JSON.stringify(this.emptyContent)))
    },
    selectedSecondProduct(productSlug) {
      this.selectedProduct(productSlug, false)
    },
    async selectedProduct(productSlug, isFirst = true) {
      if (productSlug) {
        await store.dispatch(
          FETCH_PRODUCT,
          {
            slug: productSlug
          }
        );
        if (isFirst) {
        this.postData.productSlug = this.product.slug
        this.postData.linkedProduct = this.product
        } else {
          // isSecond
          this.postData.secondProductSlug = this.product.slug
          this.postData.secondLinkedProduct = this.product
        }
      } else {
        if (isFirst) {
          this.postData.productSlug = ""
          this.postData.linkedProduct = {}
        } else {
          // isSecond
          this.postData.secondProductSlug = ""
          this.postData.secondLinkedProduct = {}
        }
      }
    },
    selectedCategory(category) {
      this.postData.category = category
    },
    deleteContent(index) {
      this.postData.content.splice(index, 1)
    },
    addVariant() {
      this.postData.variants.push(JSON.parse(JSON.stringify(this.emptyVariant)))
    },
    deleteVariant(variantIndex) {
      this.postData.variants.splice(variantIndex, 1)
    },
    toggleSplitView() {
      this.isSplitView = !this.isSplitView
    },
    cropImage(croppedImage) {
      this.postData.image.data = croppedImage
    },
    resizedCropImage(resizedCroppedImage) {
      this.postData.previewImage = resizedCroppedImage
    },
    deleteImage() {
      this.postData.image.data = ""
      this.postData.previewImage = ""
    },
    areYouSure() {
      this.$refs['modal-are-you-sure'].show()
    },
    toLocaleString(price) {
      if (price) {
        return price.toLocaleString('de-DE', {minimumFractionDigits: 2})
      } else {
        return " - "
      }
    },
    onSubmit(e) {
      e.preventDefault();
      this.$emit('submit')
    },
    onDelete() {
      this.$emit('delete')
    },
    onReset() {
      this.$emit('reset')
    }
  },
}
</script>


<style lang="scss">
.books-edit {
  overflow: hidden;
}
.edit.section {
  .RwvImageCropper {
    img {
      max-width: 80%;
      max-height: 400px;
      margin: auto;
      display: block;
      margin-bottom: 10px;
    }
  }
  .cropper {
    max-height: 26.875rem;
  }
  .form-check-label {
    margin-left: 1.25em;
  }
  .form-check-input {
    margin-left: 0;
  }
  .deactivated {
    opacity: 0.9;
  }
  form {
    margin-bottom: 3rem;
  }
  .tabs {
    min-height: 550px;
  }
}

@media screen and (min-width: $screen-md-min) {
  .splitView {
    clear: both;
    .edit.section, .RwvPost {
      width: 50%;
      float:left;
    }
    .RwvPost {
      border-left: 1px solid rgba(0,0,0,0.35);
    }
    .RwvImageCropper {
      width: 300px;
    }
    .col-md-6 {
      flex: 0 0 100%;
      max-width: 100%;
    }
  }
}
</style>
